/**
 * Role helpers — the frontend mirror of app/authz.py (Rule 12 §3).
 *
 * Keycloak roles arrive on the NextAuth session as raw strings
 * (`session.roles`). Comparisons are case-insensitive and treat `-` and `_`
 * alike, so `AP-Manager`, `ap-manager` and `ap_manager` are the same role.
 *
 * Route access:
 *  - admin / AP-Manager: everything (AP-Manager's user list is scoped server-side).
 *    Internal Audit (`/forensics`) is theirs alone.
 *  - user: everything except the admin section (unchanged legacy behaviour).
 *  - AP-Operator: Command Briefing, Dashboard, Invoice Explorer, the read-only Workbench and the
 *    read-only Contract Hub.
 *  - AP-Reviewer (business approver, plan 04): `/review` and `/review/*`. A
 *    reviewer-only session (no admin, user, AP-Manager or AP-Operator) gets
 *    the header-only reviewer shell and is kept on `/review` by the middleware.
 *    `/review` is also open to admin and AP-Manager (oversight).
 *
 * The backend (app/authz.map.json) is the source of truth; these helpers only
 * decide what to render and where to redirect.
 */

export const ROLE_ADMIN = 'admin'
export const ROLE_USER = 'user'
export const ROLE_AP_MANAGER = 'ap-manager'
export const ROLE_AP_OPERATOR = 'ap-operator'
export const ROLE_AP_REVIEWER = 'ap-reviewer'

export function normalizeRole(role: string): string {
  return role.trim().toLowerCase().replace(/_/g, '-')
}

export function normalizeRoles(roles: readonly string[] | null | undefined): Set<string> {
  return new Set((roles ?? []).filter((r) => typeof r === 'string').map(normalizeRole))
}

export function hasRole(roles: readonly string[] | null | undefined, role: string): boolean {
  return normalizeRoles(roles).has(normalizeRole(role))
}

export const isAdmin = (roles?: readonly string[] | null) => hasRole(roles, ROLE_ADMIN)
export const isApManager = (roles?: readonly string[] | null) => hasRole(roles, ROLE_AP_MANAGER)
export const isApOperator = (roles?: readonly string[] | null) => hasRole(roles, ROLE_AP_OPERATOR)
export const isPlainUser = (roles?: readonly string[] | null) => hasRole(roles, ROLE_USER)
export const isApReviewer = (roles?: readonly string[] | null) => hasRole(roles, ROLE_AP_REVIEWER)

/**
 * Reviewer-only: AP-Reviewer and none of the roles that carry the app shell.
 * Such a session sees only the reviewer shell and `/review` (plan 04 section 3.1).
 * AP-Operator also keeps the shell, so an operator who approves keeps their pages.
 */
export function isReviewerOnly(roles?: readonly string[] | null): boolean {
  return (
    isApReviewer(roles) &&
    !isAdmin(roles) &&
    !isPlainUser(roles) &&
    !isApManager(roles) &&
    !isApOperator(roles)
  )
}

/** Admin section (User / Role / Group Management, Sessions, Events, Audit, Admin Settings). */
export const canManageAdmin = (roles?: readonly string[] | null) => isAdmin(roles) || isApManager(roles)

/** Internal Audit / Risk Control Tower (`/forensics`, `/api/forensics/*`): admin and AP-Manager only. */
export const canSeeForensics = (roles?: readonly string[] | null) => isAdmin(roles) || isApManager(roles)

/** Read-only operator: Dashboard, Explorer, Workbench (view), Data Manager, Tax Hub, Settings — no AI, no admin. */
export function isReadOnly(roles?: readonly string[] | null): boolean {
  // AP-Operator is a restriction: the generic `user` grant never widens it.
  return isApOperator(roles) && !isAdmin(roles) && !isApManager(roles)
}

/**
 * The AP requester's approval notifications (`/api/ap/approval-notifications`,
 * FULL roles in app/authz.map.json): admin, user and AP-Manager.
 */
export function canReadApprovalNotifications(roles?: readonly string[] | null): boolean {
  return isAdmin(roles) || isPlainUser(roles) || isApManager(roles)
}

/** Roles that clear the "pending approval" state. */
export function isApproved(roles?: readonly string[] | null): boolean {
  return isAdmin(roles) || isPlainUser(roles) || isApManager(roles) || isApOperator(roles) || isApReviewer(roles)
}

/** Where reviewer-only sessions live (plan 04 section 3.1). */
export const REVIEWER_HOME = '/review'
export const REVIEWER_ROUTES = [REVIEWER_HOME] as const

/** Route allowlist for the read-only operator (prefix match on the first segment). */
export const OPERATOR_ROUTES = ['/', '/dashboard', '/invoices', '/workbench', '/data-manager', '/tax-hub', '/contracts', '/settings'] as const

/** Routes that only admin / AP-Manager may open. */
export const ADMIN_ROUTE_PREFIX = '/admin'
export const FORENSICS_ROUTE = '/forensics'

function stripBasePath(pathname: string): string {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  if (basePath && pathname.startsWith(basePath)) return pathname.slice(basePath.length) || '/'
  return pathname
}

function isUnder(path: string, route: string): boolean {
  return path === route || path.startsWith(`${route}/`)
}

/**
 * Paths a reviewer-only session may open: the reviewer pages, the sign-in
 * flow and static files (a last segment with a dot, e.g. /coworkers/a.jpg).
 */
export function reviewerMayOpen(pathname: string): boolean {
  const path = stripBasePath(pathname)
  if (REVIEWER_ROUTES.some((route) => isUnder(path, route))) return true
  if (path.startsWith('/auth') || path.startsWith('/api/auth')) return true
  const last = path.split('/').pop() ?? ''
  return last.includes('.')
}

/**
 * The middleware's decision for a signed-in session: the path to redirect to,
 * or null to let the request through. Only a reviewer-only session is moved,
 * and never off a page it may open, so there is no redirect loop.
 */
export function reviewerRedirect(roles: readonly string[] | null | undefined, pathname: string): string | null {
  return isReviewerOnly(roles) && !reviewerMayOpen(pathname) ? REVIEWER_HOME : null
}

/** The landing page for a session: `/review` for reviewer-only users, `/` otherwise. */
export function homeFor(roles?: readonly string[] | null): string {
  return isReviewerOnly(roles) ? REVIEWER_HOME : '/'
}

/**
 * Whether the given roles may open `pathname` (an app route, not an API path).
 * Auth routes are always allowed so the sign-in flow is never blocked.
 */
export function canAccess(pathname: string, roles?: readonly string[] | null): boolean {
  const path = stripBasePath(pathname)
  if (path.startsWith('/auth')) return true
  if (isReviewerOnly(roles)) return reviewerMayOpen(path)
  if (isAdmin(roles) || isApManager(roles)) return true
  if (path === ADMIN_ROUTE_PREFIX || path.startsWith(`${ADMIN_ROUTE_PREFIX}/`)) return false
  if (isUnder(path, FORENSICS_ROUTE)) return canSeeForensics(roles)
  // Approvals: admin and AP-Manager (above) for oversight, and anyone who is a reviewer.
  if (REVIEWER_ROUTES.some((route) => isUnder(path, route))) return isApReviewer(roles)
  if (isReadOnly(roles)) {
    return OPERATOR_ROUTES.some((route) => (route === '/' ? path === '/' : isUnder(path, route)))
  }
  // `user` (and any unrecognised role set) keeps the legacy behaviour: every
  // non-admin page; the backend still enforces the API side.
  return true
}
